import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import Nav from "@/components/Nav";
import JourneyMap from "@/components/journey/JourneyMap";
import { track } from "@/lib/analytics";

const STEPS = [
  {
    n: "01",
    title: "Understand what you're applying for",
    when: "Junior year (or any time)",
    body: "There are two main financial-aid applications: the FAFSA and the CSS Profile. Most schools use FAFSA only. About 200 highly selective schools also require the CSS Profile, which goes deeper into your family's finances. Know which one(s) your schools require before you start gathering documents.",
    list: {
      title: "FAFSA vs CSS Profile",
      items: [
        ["FAFSA",       "Required by all Title IV schools. Simpler formula. Opens October 1. Free."],
        ["CSS Profile", "Required by ~200 private colleges. More detailed. Opens October 1. $25 first school, $16 each additional."],
        ["Heads up",    "CSS asks about home equity, business assets, and non-custodial parent income. Can result in MORE or LESS aid than FAFSA alone, depends on the school."],
      ],
    },
    tools: [{ to: "/chat", label: "Ask the AI →" }],
  },
  {
    n: "02",
    title: "Gather your documents",
    when: "September-October (before FAFSA opens)",
    body: "FAFSA uses your family's tax information from two years ago, for 2025-26, that's your 2023 return. Having everything ready before October 1 means you can file the day FAFSA opens, which matters in states where state grants are first-come, first-served.",
    list: {
      title: "Documents you need",
      items: [
        ["Student",  "SSN (or A-Number), FSA ID created at StudentAid.gov before October."],
        ["Parents",  "FSA ID for one parent, 2023 Form 1040, W-2s, untaxed-income records, current bank balances, investment values (NOT retirement)."],
        ["Schools",  "Federal school codes, look them up at StudentAid.gov."],
        ["NOT needed", "Retirement balances, primary home value, life insurance, all excluded."],
      ],
    },
    callout: {
      title: "Tip: use the Side-by-Side tool",
      body: "Keep your tax return visible on one side while you use the AI Chat or SAI Estimator on the other. Nothing you upload leaves your browser.",
      cta: { to: "/sidebyside", label: "Open Side-by-Side →" },
    },
    tools: [{ to: "/sidebyside", label: "Open Side-by-Side →" }],
  },
  {
    n: "03",
    title: "File as early as possible",
    when: "October 1, file in the first week",
    body: "FAFSA opens October 1 every year. Many states give grant money first-come, first-served until funds run out. October vs. February can be the difference between thousands of dollars in state grants and nothing. Your state deadline is almost always EARLIER than the federal one (June 30), missing it forfeits state grants for the year.",
    list: {
      title: "Reality-check timeline",
      items: [
        ["Oct 1",         "FAFSA opens, file now."],
        ["Nov-Dec",       "Earliest state deadlines (TN Dec 1)."],
        ["Dec-Mar",       "Most other state deadlines."],
        ["Mar-Apr",       "School aid letters arrive."],
        ["May 1",         "National Decision Day, commit to a school."],
      ],
    },
    tools: [{ to: "/deadlines", label: "Check Your State's Deadline →" }],
  },
  {
    n: "04",
    title: "Understand your SAI",
    when: "After filing, while waiting for awards",
    body: "After you file FAFSA, the government calculates your Student Aid Index (SAI), a number that represents how much your family is expected to contribute. Lower SAI = more need-based aid. Your SAI is NOT what you pay. Each school takes the same SAI and builds different packages.",
    list: {
      title: "What your SAI means",
      items: [
        ["SAI 0",            "Maximum need. Full Pell Grant ($7,395/yr) + significant need-based institutional aid at most schools."],
        ["SAI 1-6,500",      "Partial Pell + need-based institutional aid likely."],
        ["SAI 6,500-20,000", "No Pell Grant. Some schools still offer institutional grants."],
        ["SAI 20,000+",      "Limited need-based aid. Merit scholarships become your primary lever."],
      ],
    },
    tools: [{ to: "/estimator", label: "Run the SAI Estimator →" }],
  },
  {
    n: "05",
    title: "Decode your award letters",
    when: "March-April",
    body: "Aid letters are intentionally hard to compare. Schools use different terms, formats, and some bundle loans with grants to make their offer look bigger. The only number that matters is your NET COST: what you actually pay after subtracting free money.",
    list: {
      title: "How to read an award letter",
      items: [
        ["Free money",      "Pell Grant, institutional grants, state grants, scholarships, these reduce your cost."],
        ["Money you earn",  "Work-study, you have to actually work for it."],
        ["Money you borrow","Subsidized + unsubsidized loans, PLUS Loan, must be repaid with interest."],
        ["The real formula","COA − Grants − Scholarships = your annual cost. Loans are separate."],
      ],
    },
    tools: [
      { to: "/decoder", label: "Decode My Aid Letter →" },
      { to: "/compare", label: "Compare Schools Side-by-Side →" },
    ],
  },
  {
    n: "06",
    title: "Maximize what you receive",
    when: "Ongoing, before, after, every year",
    body: "Most families accept the first offer they receive without realizing three things: aid can be appealed, low-competition scholarships exist, and the same application filed earlier gets more money in rolling-deadline states.",
    list: {
      title: "Three levers",
      items: [
        ["Appeal",     "If income changed, medical bills, job loss, divorce, file an appeal letter. The FAFSA uses two-year-old tax data; an appeal explains what changed."],
        ["Negotiate",  "Schools have leverage to match competing offers. Ask politely with the other letter attached."],
        ["Scholarships","Local + employer scholarships have 5-50 applicants vs. thousands for national ones. Reapply every year."],
      ],
    },
    tools: [
      { to: "/appeal",       label: "Write an Appeal Letter →" },
      { to: "/scholarships", label: "Find Scholarships →" },
    ],
  },
  {
    n: "07",
    title: "Know what you're borrowing",
    when: "Before May 1 commitment",
    body: "Loans aren't free money, every dollar you borrow costs more than a dollar to repay. Before you commit, understand exactly what the loan portion of your package will cost over time.",
    list: {
      title: "2025-26 federal loan rates",
      items: [
        ["Direct Subsidized (undergrad)",   "6.53%, no interest while in school."],
        ["Direct Unsubsidized (undergrad)", "6.53%, interest starts immediately."],
        ["Direct Unsubsidized (grad)",      "8.05%."],
        ["PLUS Loan (parents)",             "9.08%, exhaust everything else first."],
      ],
    },
    tools: [{ to: "/loans", label: "Loan Reality Check →" }],
  },
];

export default function JourneyPage() {
  useEffect(() => { track.tool_opened("journey"); }, []);

  return (
    <div className="theme-light min-h-screen flex flex-col bg-white">
      <Nav />

      <main className="flex-1 bg-white">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm mb-7 text-gray-500 hover:text-gray-900 transition-colors"
            data-testid="journey-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to AidWaive
          </Link>
          <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">The Journey</div>
          <h1 className="font-heading leading-[1.05] mb-4 text-gray-900" style={{ fontSize: "clamp(2.4rem, 4.5vw, 3.6rem)", letterSpacing: "-0.012em" }}>
            Your financial-aid journey, <em className="italic" style={{ color: "#1d4ed8" }}>start to finish.</em>
          </h1>
          <p className="text-base sm:text-lg max-w-2xl leading-relaxed text-gray-500">
            From your first question to your first semester, every step, every document, every deadline. No consultant needed.
          </p>

          {/* Section A, calendar map */}
          <div className="mt-12">
            <JourneyMap />
          </div>

          {/* Connector */}
          <div className="text-center my-12 sm:my-16">
            <div className="text-sm" style={{ color: "#9ca3af" }}>Explore each step in detail</div>
            <div className="mt-3 flex justify-center" aria-hidden>
              <span style={{ width: 1, height: 40, background: "#e5e7eb", display: "block" }} />
            </div>
          </div>

          {/* Section B, vertical step cards */}
          <div className="space-y-4">
            {STEPS.map((s) => <StepCard key={s.n} step={s} />)}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 rounded-2xl p-8 sm:p-12 text-center" style={{ background: "#ffffff", border: "1px solid #e5e7eb" }} data-testid="journey-bottom-cta">
            <h2 className="font-heading text-3xl sm:text-4xl leading-[1.1]" style={{ color: "#111928" }}>
              Ready to <em className="italic" style={{ color: "#1d4ed8" }}>actually start?</em>
            </h2>
            <p className="mt-3 text-sm sm:text-base max-w-md mx-auto" style={{ color: "#6b7280" }}>
              Pick your starting point. Most families start with the AI chat or by checking their state's deadline.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center">
              <Link to="/chat" data-testid="journey-cta-chat"
                className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-md text-sm font-medium hover:opacity-90"
                style={{ background: "#1d4ed8", color: "#ffffff" }}>
                Ask the AI <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/deadlines" data-testid="journey-cta-deadlines"
                className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-md text-sm font-medium"
                style={{ border: "1px solid #e5e7eb", color: "#6b7280" }}>
                Check my deadline
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function StepCard({ step }) {
  return (
    <article
      className="rounded-xl p-6 sm:p-8 transition-colors"
      style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
      data-testid={`journey-step-${step.n}`}>
      <div className="flex items-baseline gap-4 flex-wrap">
        <div className="font-mono text-xs" style={{ color: "#9ca3af" }}>{step.n}</div>
        <div className="px-2 py-0.5 rounded-md text-[11px] font-medium uppercase tracking-[0.08em]"
          style={{ background: "#fef3c7", color: "#92400e" }}>
          {step.when}
        </div>
      </div>

      <h2 className="font-heading text-2xl sm:text-3xl mt-3 leading-[1.15]" style={{ color: "#111928" }}>
        {step.title}
      </h2>

      <p className="mt-4 leading-relaxed text-sm sm:text-base" style={{ color: "#6b7280" }}>
        {step.body}
      </p>

      {step.list && (
        <div className="mt-6 rounded-lg p-4 sm:p-5" style={{ background: "#f3f4f6", border: "1px solid #e5e7eb" }}>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-3" style={{ color: "#9ca3af" }}>{step.list.title}</div>
          <dl className="space-y-2 text-sm">
            {step.list.items.map(([k, v], i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-3">
                <dt className="font-medium" style={{ color: "#111928" }}>{k}</dt>
                <dd style={{ color: "#6b7280" }}>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {step.callout && (
        <div className="mt-5 rounded-lg p-4" style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}>
          <div className="font-medium text-sm" style={{ color: "#1e40af" }}>💡 {step.callout.title}</div>
          <p className="mt-1.5 text-sm" style={{ color: "#1e40af", opacity: 0.85 }}>{step.callout.body}</p>
          <Link to={step.callout.cta.to} className="text-sm font-medium mt-3 inline-flex items-center gap-1"
            style={{ color: "#1d4ed8" }}>
            {step.callout.cta.label}
          </Link>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {step.tools.map(t => (
          <Link key={t.to} to={t.to}
            className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
            style={{ color: "#1d4ed8" }}
            data-testid={`journey-step-${step.n}-tool-${t.to.slice(1)}`}>
            {t.label}
          </Link>
        ))}
      </div>
    </article>
  );
}
